import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { HttpClient } from '@angular/common/http';
import { ClientAuthError, Constants, CryptoUtils, AuthError } from 'msal';
import { ServerHashParamKeys } from 'msal/lib-commonjs/utils/Constants';
import { environment } from 'src/environments/environment';
import { ConfigLoaderService } from '../shared/config-loader.service';

export const AzureManagementUrl = "https://management.azure.com";
export const GraphApiUrl = "https://graph.microsoft.com";
export const DefaultScope = "api://angr-swisscom-smartworkplacev2-test/default";

const decodeURLParams = search => {
  let qIndex = search.indexOf("?");
  let hashIndex = search.indexOf("#");
  const hashes = search.slice((qIndex > hashIndex ? qIndex : hashIndex) + 1).split("&");
  return hashes.reduce((params, hash) => {
      const split = hash.indexOf("=");

      if (split < 0) {
          return Object.assign(params, {
              [hash]: null
          });
      }

      const key = hash.slice(0, split);
      const val = hash.slice(split + 1);
      var decodeVal = decodeURIComponent(val);
      // Azure AD return URL encoded with + instead space
      decodeVal = decodeVal.replace(/\+/g, " ");
      return Object.assign(params, { [key]: decodeVal});
  }, {});
};

@Injectable({
  providedIn: 'root'
})
export class AzureadHelperService {

  constructor(private authService: MsalService, private http: HttpClient, private configService: ConfigLoaderService) { }

  getTenants(){
    return this.http.get(AzureManagementUrl + '/tenants?api-version=2017-08-01');
  }

  adminConsenstPopup(appId: string){
    return new Promise((resolve, reject) => {
      let popUpWindow = null;
      try{
        popUpWindow = this.openPopup("about:blank", "msal", Constants.popUpWidth, Constants.popUpHeight);
      }
      catch(e){
        reject(e);
        return;
      }
      var acc = this.authService.getAccount();
      return this.navigateWindow("https://login.microsoftonline.com/" + acc.idToken.tid + "/adminConsent?client_id=" + appId + "&redirect_uri=" + this.configService.Configuration.msal.redirectUri, popUpWindow).then(
        () => resolve(),
        (e) => reject(e)
      );
    });
  }

  /**
   * MSAL do not support admin consent, so we ahve to use old endpoint to make it work
   * @param clientId 
   */
  loginAdminConsenstPopup(clientId: string){

    return new Promise((resolve, reject) => {
      let popUpWindow = null;
      try{
        popUpWindow = this.openPopup("about:blank", "msal", Constants.popUpWidth, Constants.popUpHeight);
      }
      catch(e){
        reject(e);
        return;
      }
      var acc = this.authService.getAccount();

      return this.navigateWindow(this.buildUrlForLoginWithAdminConsent(clientId), popUpWindow).then(
        () => resolve(),
        (e) => reject(e)
      );
    });
  }

  private buildUrlForLoginWithAdminConsent(clientId){
    //https://login.microsoftonline.com/common/oauth2/authorize?response_type=id_token&client_id=5f4d4774-ef98-4858-bde7-07d6ca78a0f1&redirect_uri=http%3A%2F%2Flocalhost%3A4200&response_mode=fragment&prompt=admin_consent&nonce=19adfa07-b25d-4f1e-bf71-fe11e090887e

    return "https://login.microsoftonline.com/common/oauth2/authorize?"
       + "response_type=id_token"
       + "&client_id=" + clientId
       + "&redirect_uri=" + this.configService.Configuration.msal.redirectUri
       // use frragment mode to get errors and other info from query string
       + "&response_mode=fragment"
       + "&prompt=admin_consent"
       // TODO Save this nonce and check it on response
       + "&nonce=" + CryptoUtils.createNewGuid();
  }

  private navigateWindow(urlNavigate: string, popupWindow?: Window) {
    // Navigate if valid URL
    if (urlNavigate && urlNavigate !== "") {
        const navigateWindow: Window = popupWindow ? popupWindow : window;
        navigateWindow.location.assign(urlNavigate);

        return this.monitorWindowForHash(navigateWindow, 2000, urlNavigate).finally(() => {
          if(navigateWindow && !navigateWindow.closed) 
            navigateWindow.close();
        });
    }
    
  }

  private monitorWindowForHash(contentWindow: Window, timeout: number, urlNavigate: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const maxTicks = timeout / 50;
        let ticks = 0;

        const intervalId = setInterval(() => {
            if (contentWindow.closed) {
                clearInterval(intervalId);
                reject(ClientAuthError.createUserCancelledError());
                return;
            }

            let href;
            try {
                /*
                 * Will throw if cross origin,
                 * which should be caught and ignored
                 * since we need the interval to keep running while on STS UI.
                 */
                href = contentWindow.location.href;
            } catch (e) {}

            // Don't process blank pages or cross domain
            if (!href || href === "about:blank") {
                return;
            }

            // Only run clock when we are on same domain
            ticks++;

            if (this.urlContainsQueryParams(contentWindow.location.search)) {
                clearInterval(intervalId);
                var parameters = decodeURLParams(contentWindow.location.search);
                console.log(parameters);
                if(parameters.hasOwnProperty(ServerHashParamKeys.ERROR)){
                  reject(new AuthError(parameters.error, parameters.error_description))
                }else{
                  resolve(parameters);
                }
                
            }
            else if (this.urlContainsQueryParams(contentWindow.location.hash)) {
              clearInterval(intervalId);
              var parameters = decodeURLParams(contentWindow.location.hash);
              console.log(parameters);
              if(parameters.hasOwnProperty(ServerHashParamKeys.ERROR)){
                reject(new AuthError(parameters.error, parameters.error_description))
              }else{
                resolve(parameters);
              }
              
          }
             else if (ticks > maxTicks) {
                clearInterval(intervalId);
                reject(ClientAuthError.createTokenRenewalTimeoutError()); // better error?
            }
        }, 50);
    });
  } 

  private urlContainsQueryParams(queryPart: string): boolean {
        if(!queryPart) return false;
        if(queryPart.startsWith("#"))
          queryPart = queryPart.substr(1, queryPart.length - 1)
        const parameters = decodeURLParams(queryPart);
        return (
            parameters.hasOwnProperty("error_description") ||
            parameters.hasOwnProperty("error") ||
            parameters.hasOwnProperty("admin_consent") ||
            parameters.hasOwnProperty("id_token") ||
            parameters.hasOwnProperty("tenant")
        );
    }


  private openPopup(urlNavigate: string, title: string, popUpWidth: number, popUpHeight: number) {
    try {
        /**
         * adding winLeft and winTop to account for dual monitor
         * using screenLeft and screenTop for IE8 and earlier
         */
        const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
        const winTop = window.screenTop ? window.screenTop : window.screenY;
        /**
         * window.innerWidth displays browser window"s height and width excluding toolbars
         * using document.documentElement.clientWidth for IE8 and earlier
         */
        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
        const left = ((width / 2) - (popUpWidth / 2)) + winLeft;
        const top = ((height / 2) - (popUpHeight / 2)) + winTop;

        // open the window
        const popupWindow = window.open(urlNavigate, title, "width=" + popUpWidth + ", height=" + popUpHeight + ", top=" + top + ", left=" + left);
        if (!popupWindow) {
            throw ClientAuthError.createPopupWindowError();
        }
        if (popupWindow.focus) {
            popupWindow.focus();
        }

        return popupWindow;
    } catch (e) {
        throw ClientAuthError.createPopupWindowError(e.toString());
    }
}

}
